import React from "react";
import { FiSliders, FiZap, FiAward, FiArrowRight } from "react-icons/fi";
import { useBuilder } from "./BuilderContext";
import { cx } from "./ui";

export default function RuleFlowPipeline({ draft }) {
  const { schemeIds = [], wallets = [] } = useBuilder();

  const schemeNames = schemeIds.map(
    (id) => wallets.find((w) => String(w.scheme_id) === String(id))?.name || `Scheme #${id}`
  );
  const firstSchemeName = schemeNames[0] || null;
  const hasSchemes = schemeIds.length > 0;

  const creditAction = (draft.actions || []).find((a) => a.type === "CREDIT" || a.type === "CREDIT_DYNAMIC");
  const walletType = creditAction?.wallet_type;
  const walletName = !walletType || walletType === "default"
    ? "Main Wallet"
    : wallets.find((w) => String(w.scheme_id) === String(walletType))?.name || `Scheme #${walletType}`;

  const linked = hasSchemes && !!walletType && walletType !== "default" && schemeIds.includes(String(walletType));
  const mismatched = hasSchemes && !!walletType && walletType !== "default" && !schemeIds.includes(String(walletType));

  const conn1 = hasSchemes ? "bg-blue-300" : "bg-gray-200";
  const conn1Text = hasSchemes ? "text-blue-400" : "text-gray-300";
  const conn2 = linked ? "bg-emerald-300" : mismatched ? "bg-amber-300" : "bg-gray-200";
  const conn2Text = linked ? "text-emerald-400" : mismatched ? "text-amber-400" : "text-gray-300";
  const walletNodeCls = linked
    ? "bg-emerald-50 border-emerald-200"
    : mismatched
    ? "bg-amber-50 border-amber-200"
    : "bg-gray-50 border-gray-200";

  return (
    <div className="rounded-2xl border border-gray-200/80 bg-white overflow-hidden shadow-2xs">
      <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200">
        <FiSliders className="h-3.5 w-3.5 text-gray-500" />
        <h4 className="text-xs font-bold uppercase tracking-wider text-gray-600">Rule Flow Pipeline</h4>
        {linked && (
          <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Scheme &amp; wallet linked
          </span>
        )}
        {mismatched && (
          <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
            Scheme / wallet mismatch
          </span>
        )}
      </div>

      <div className="px-5 py-4 flex items-stretch gap-1 overflow-x-auto">
        {/* Event Node */}
        <div className="flex flex-col justify-center gap-1 bg-indigo-50 border border-indigo-200 rounded-xl px-3 py-2.5 min-w-[110px] shrink-0">
          <div className="flex items-center gap-1.5">
            <FiZap className="h-3 w-3 text-indigo-500 shrink-0" />
            <span className="text-[9px] font-bold uppercase tracking-widest text-indigo-400">Event</span>
          </div>
          <span className="text-xs font-bold text-indigo-900 truncate">{draft.event_type || "—"}</span>
          <span className="text-[10px] text-indigo-400">Trigger</span>
        </div>

        {/* Connector 1 */}
        <div className="flex items-center gap-0 shrink-0">
          <div className={cx("h-0.5 w-6", conn1)} />
          <FiArrowRight className={cx("h-3.5 w-3.5 shrink-0", conn1Text)} />
        </div>

        {/* Scheme Node */}
        <div className={cx(
          "flex flex-col justify-center gap-1 rounded-xl px-3 py-2.5 min-w-[120px] shrink-0 border",
          hasSchemes ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-dashed border-gray-200"
        )}>
          <div className="flex items-center gap-1.5">
            <span className={cx("w-2 h-2 rounded-full shrink-0", hasSchemes ? "bg-blue-500" : "bg-gray-300")} />
            <span className={cx("text-[9px] font-bold uppercase tracking-widest", hasSchemes ? "text-blue-400" : "text-gray-400")}>
              {schemeIds.length > 1 ? `Schemes (${schemeIds.length})` : "Scheme"}
            </span>
          </div>
          {hasSchemes
            ? <div className="space-y-0.5">
                {schemeNames.map((name, i) => (
                  <div key={i} className="text-xs font-bold text-blue-900 truncate">{name}</div>
                ))}
              </div>
            : <span className="text-xs font-bold text-gray-400">Any / not pinned</span>
          }
          <span className={cx("text-[10px]", hasSchemes ? "text-blue-400" : "text-gray-400")}>Conditions target</span>
        </div>

        {/* Connector 2 */}
        <div className="flex items-center gap-0 shrink-0">
          <div className={cx("h-0.5 w-6", conn2)} />
          <FiArrowRight className={cx("h-3.5 w-3.5 shrink-0", conn2Text)} />
        </div>

        {/* Wallet Node */}
        <div className={cx("flex flex-col justify-center gap-1 rounded-xl px-3 py-2.5 min-w-[120px] shrink-0 border", walletNodeCls)}>
          <div className="flex items-center gap-1.5">
            <FiAward className={cx("h-3 w-3 shrink-0", linked ? "text-emerald-500" : mismatched ? "text-amber-500" : "text-gray-400")} />
            <span className={cx("text-[9px] font-bold uppercase tracking-widest", linked ? "text-emerald-400" : mismatched ? "text-amber-400" : "text-gray-400")}>Wallet</span>
          </div>
          <span className={cx("text-xs font-bold truncate", linked ? "text-emerald-900" : mismatched ? "text-amber-900" : "text-gray-600")}>
            {creditAction ? walletName : "No credit action"}
          </span>
          <span className={cx("text-[10px]", linked ? "text-emerald-400" : mismatched ? "text-amber-400" : "text-gray-400")}>Action wallet</span>
        </div>
      </div>

      {mismatched && (
        <div className="px-5 pb-3">
          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
            <strong>Mismatch:</strong>{" "}
            {schemeIds.length === 1
              ? <>Conditions target <strong>{firstSchemeName}</strong></>
              : <>Conditions target <strong>{schemeIds.length} schemes</strong></>
            }{" "}
            but the action credits <strong>{walletName}</strong> (not in the target set). Verify this is intentional.
          </p>
        </div>
      )}
    </div>
  );
}
